import { useState, useRef, useEffect } from "react";
import { useAtom } from "jotai";
import { sentEmailAddressesAtom } from "../-Atoms";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";

const AppleMailContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "flex-start",
  padding: "6px 8px",
  minHeight: "32px", // Reduced height
  width: "100%",
  position: "relative",
  cursor: "text",
  flexWrap: "wrap",
  backgroundColor: "transparent", // Remove background color
  borderRadius: "0.5rem", // Set border radius to 0.5
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.02)",
  },
  "&:focus-within": {
    backgroundColor: "rgba(0, 0, 0, 0.02)",
  },
}));

const StyledChip = styled(Chip)(() => ({
  backgroundColor: "#007AFF", // Apple blue
  color: "white",
  margin: "1px 2px",
  height: "20px", // Smaller height
  fontSize: "12px",
  borderRadius: "0.2rem", // Set border radius to 0.5
  "& .MuiChip-deleteIcon": {
    color: "rgba(255, 255, 255, 0.7)",
    fontSize: "16px",
    "&:hover": {
      color: "white",
    },
  },
  "&:hover": {
    backgroundColor: "#0056CC", // Darker blue on hover
  },
}));

const HiddenInput = styled("input")({
  border: "none",
  outline: "none", // Remove outline when active
  fontSize: "14px",
  flexGrow: 1,
  minWidth: "50px",
  backgroundColor: "transparent", // Remove background
  padding: "2px 4px",
  margin: "1px 2px",
  fontFamily: "inherit",
  height: "20px", // Smaller height
  "&:focus": {
    outline: "none", // Ensure no outline on focus
  },
});

const SuggestionsPaper = styled(Paper)(({ theme }) => ({
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  zIndex: 1000,
  maxHeight: "200px",
  overflowY: "auto",
  marginTop: "2px",
  backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f5f5f5', // Light grey that adapts to theme
  border: `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`,
  borderRadius: "0.5rem",
  boxShadow: theme.shadows[8],
  color: theme.palette.text.primary, // Use theme text color
}));

const EmailField = ({
  emails = [],
  setEmails,
  kind,
  visible = true,
  marginRight,
  disabled = false,
}) => {
  const [sentEmailAddresses] = useAtom(sentEmailAddressesAtom);
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const suggestionsRef = useRef(null);

  const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  // Filter suggestions based on input
  useEffect(() => {
    if (inputValue.trim().length > 0) {
      const existingEmails = emails.map(email => 
        typeof email === "object" ? email.email : email
      );
      
      const filtered = sentEmailAddresses.filter(email => 
        email.toLowerCase().includes(inputValue.toLowerCase()) &&
        !existingEmails.includes(email)
      );
      
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
      setSelectedSuggestionIndex(-1);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  }, [inputValue, sentEmailAddresses, emails]);

  // Scroll to selected suggestion
  useEffect(() => {
    if (suggestionsRef.current && selectedSuggestionIndex >= 0) {
      const suggestionItems = suggestionsRef.current.querySelectorAll('.suggestion-item');
      const selectedItem = suggestionItems[selectedSuggestionIndex];
      if (selectedItem) {
        selectedItem.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [selectedSuggestionIndex]);

  const validateEmail = (email) => {
    return emailRegex.test(email);
  };

  const addEmailChip = (email) => {
    if (email.trim() && validateEmail(email.trim())) {
      const emailObj = typeof emails[0] === "object" 
        ? { email: email.trim() } 
        : email.trim();
      
      setEmails([...emails, emailObj]);
      setInputValue("");
      setShowSuggestions(false);
    }
  };

  const removeEmailChip = (index) => {
    if (disabled) return;
    const newEmails = [...emails];
    newEmails.splice(index, 1);
    setEmails(newEmails);
  };

  const handleInputChange = (e) => {
    if (disabled) return;
    const value = e.target.value;
    
    // Handle comma or semicolon separated emails
    if (value.includes(',') || value.includes(';')) {
      const separator = value.includes(',') ? ',' : ';';
      const emailsToAdd = value.split(separator);
      const lastEmail = emailsToAdd.pop().trim();
      
      emailsToAdd.forEach(email => {
        const trimmedEmail = email.trim();
        if (trimmedEmail && validateEmail(trimmedEmail)) {
          addEmailChip(trimmedEmail);
        }
      });
      
      setInputValue(lastEmail);
      return;
    }
    
    setInputValue(value);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      
      if (showSuggestions && selectedSuggestionIndex >= 0) {
        // Select highlighted suggestion
        addEmailChip(suggestions[selectedSuggestionIndex]);
      } else if (inputValue.trim()) {
        // Add current input as email
        addEmailChip(inputValue.trim());
      }
      
      return;
    }

    if (e.key === "Backspace" && inputValue === "" && emails.length > 0) {
      // Remove last chip when backspace is pressed on empty input
      removeEmailChip(emails.length - 1);
      return;
    }

    if (showSuggestions && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedSuggestionIndex(prev => {
          if (prev < suggestions.length - 1) {
            return prev + 1;
          } else {
            return 0; // Cycle back to first item
          }
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedSuggestionIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (prev === 0) {
            return -1; // Go back to no selection
          } else {
            return suggestions.length - 1; // Cycle to last item
          }
        });
      } else if (e.key === "Escape") {
        e.preventDefault();
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    }
  };

  const handleContainerClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    addEmailChip(suggestion);
  };

  const handleInputBlur = () => {
    // Delay hiding suggestions to allow clicking on them
    setTimeout(() => {
      if (inputValue.trim() && validateEmail(inputValue.trim())) {
        addEmailChip(inputValue.trim());
      }
      setShowSuggestions(false);
    }, 150);
  };

  const getEmailDisplay = (emailItem) => {
    if (typeof emailItem === "object") {
      return emailItem.name || emailItem.email;
    }
    return emailItem;
  };

  const getEmailValue = (emailItem) => {
    if (typeof emailItem === "object") {
      return emailItem.email;
    }
    return emailItem;
  };

  const emailsContainerStyle = {
    minHeight: 10,
    display: visible ? "flex" : "none",
    marginBottom: 2,
    marginTop: 2,
    marginRight: marginRight,
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  };

  return (
    <div style={emailsContainerStyle}>
      <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
        <p
          style={{
            marginTop: 8,
            marginBottom: 8,
            marginRight: 10,
            fontSize: "0.8em",
            minWidth: "30px",
          }}
        >
          {kind}:
        </p>
        
        <Box sx={{ flex: 1, position: "relative" }} ref={containerRef}>
          <AppleMailContainer onClick={handleContainerClick}>
            {/* Render email chips */}
            {emails.map((emailItem, index) => {
              if (emailItem && (emailItem.email || typeof emailItem === "string")) {
                return (
                  <StyledChip
                    key={`email-chip-${kind}-${index}`}
                    label={getEmailDisplay(emailItem)}
                    onDelete={disabled ? undefined : () => removeEmailChip(index)}
                    title={getEmailValue(emailItem)}
                    size="small"
                  />
                );
              }
              return null;
            })}
            
            {/* Input field */}
            <HiddenInput
              ref={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={handleInputBlur}
              // placeholder={emails.length === 0 ? `Enter ${kind.toLowerCase()} addresses...` : ""}
              disabled={disabled}
              style={{ opacity: disabled ? 0.6 : 1 }}
            />
          </AppleMailContainer>
          
          {/* Suggestions dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <SuggestionsPaper ref={suggestionsRef}>
              <List dense>
                {suggestions.map((suggestion, index) => (
                  <ListItem
                    key={`suggestion-${index}`}
                    className="suggestion-item"
                    button
                    selected={index === selectedSuggestionIndex}
                    onClick={() => handleSuggestionClick(suggestion)}
                    sx={{
                      py: 0.75,
                      px: 1.5,
                      cursor: "pointer",
                      borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
                      color: (theme) => theme.palette.text.primary,
                      "&:hover": {
                        backgroundColor: "rgba(0, 122, 255, 0.1)",
                      },
                      "&.Mui-selected": {
                        backgroundColor: "#007AFF !important",
                        color: "white !important",
                        "&:hover": {
                          backgroundColor: "#0056CC !important",
                        },
                        "& .MuiListItemText-primary": {
                          color: "white !important",
                        },
                      },
                    }}
                  >
                    <ListItemText 
                      primary={suggestion} 
                      sx={{
                        "& .MuiListItemText-primary": {
                          fontSize: "14px",
                          fontWeight: index === selectedSuggestionIndex ? 500 : 400,
                        }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </SuggestionsPaper>
          )}
        </Box>
      </Box>
    </div>
  );
};

export default EmailField;